import { AccountUserSchema } from './account.schema.js';
// ---------------------------------------------------------------------------
// Account status transitions
// ---------------------------------------------------------------------------
const ACCOUNT_STATUS_TRANSITIONS = {
    'active': ['inactive', 'suspended', 'closed'],
    'inactive': ['active', 'closed'],
    'suspended': ['active', 'closed'],
    'closed': [],
};
export function canTransitionAccount(from, to) {
    return ACCOUNT_STATUS_TRANSITIONS[from]?.includes(to) ?? false;
}
// ---------------------------------------------------------------------------
// Member state changes — the one decider (SOR-590)
// ---------------------------------------------------------------------------
/**
 * The five member states in vocabulary order — the runtime view of the
 * `MemberState` union, read from the schema's own `status` enum options rather
 * than re-listed here. A state added to `AccountUserSchema.status` enters this
 * list by construction, which is what the admin state control and the R1
 * property shard sweep; a second hand-written list could only drift from it.
 */
export const MEMBER_STATES = AccountUserSchema.shape.status.options;
/**
 * The states no staff move may reach or leave. `suspended` is where the intake
 * actor parks a row it has disabled; `erased` is terminal. The pair is named
 * for the readers that assert it as a fact — the SPEC-SOR-589 R1 property shard
 * and the state-machine suite pin it exactly. The admin control needs no such
 * list: it derives a row's options from the decider, so a reserved row is
 * read-only by the same fact that makes it unmovable.
 */
export const RESERVED_MEMBER_STATES = [
    'suspended',
    'erased',
];
/**
 * The moves the STAFF actor may apply, as (prior, target) pairs. This is the
 * ONE staff relation (SOR-591): the retired `canTransitionAccountUser` and its
 * `ACCOUNT_USER_STATUS_TRANSITIONS` table are gone, and the decider is the
 * only staff reader left, so the relation is stated exactly once, here. A
 * staff move the portal offers is therefore exactly a move the API applies —
 * no second table can drift from this one.
 *
 * `suspended` and `erased` appear on neither side: the member-state vocabulary
 * is five-wide, so the two reserved states are absent from the list rather
 * than carried as empty entries — `RESERVED_MEMBER_STATES` names them, and the
 * decider refuses every staff move that names one.
 */
const STAFF_MOVES = [
    ['pending', 'active'],
    ['active', 'inactive'],
    ['inactive', 'active'],
];
/**
 * The moves the INTAKE actor may apply, as (prior, target) pairs. Both movable
 * actors' rules are data — this list and `STAFF_MOVES` above — so each is
 * readable at a glance rather than inferred from branches. The erasure actor's
 * single move is "into `erased`, from anywhere that is not already erased",
 * which a pair list cannot express without naming every non-erased prior — so
 * it is stated in the decider instead of padded out here.
 *
 * `intake`'s `active → inactive` is deliberate and is NOT the staff
 * `active → inactive`: intake disables a row as part of its own flow, and the
 * verdict is the same move whichever actor asks. What intake may never do is
 * move a row OUT of `inactive` — an `inactive` member is one staff disabled,
 * and no intake event revives it (`IntakeNeverRevivesAStaffDisabledRow`).
 */
const INTAKE_MOVES = [
    ['active', 'suspended'],
    ['suspended', 'active'],
    ['suspended', 'inactive'],
    ['active', 'inactive'],
];
/**
 * The ONE decider for every member-state change (SOR-590). Every surface that
 * asks "may this row move there, for this actor?" asks here — the admin state
 * control, the API write path, the property shard — so there is no second
 * transition table to drift from this one.
 *
 * `unchanged` exactly when `prior` equals `target`, whoever asks: an
 * instruction that names the state the row already holds has nothing to apply,
 * and that stays true even for an actor that could never have *applied* that
 * move (erasing an already-erased row, staffing a row to the state it holds).
 * Because that arm is tested first, every arm below is reached only by a
 * genuine move, so a no-op is never reported as `applied`.
 *
 * `applied` exactly for: an erasure from any non-erased state to `erased`; the
 * staff moves pending → active, active → inactive and inactive → active; and
 * the intake moves active → suspended, suspended → active, suspended →
 * inactive and active → inactive. Every other combination is `refused` —
 * including every staff move naming `suspended` or `erased`, which are
 * reserved out of the staff vocabulary entirely.
 *
 * Both movable arms read their relation as data (`STAFF_MOVES` /
 * `INTAKE_MOVES`), so the question each answers has one statement and no
 * reader can disagree with it.
 */
export function decideMemberStateChange(prior, target, actor) {
    if (prior === target)
        return 'unchanged';
    if (actor === 'erasure') {
        return target === 'erased' ? 'applied' : 'refused';
    }
    const moves = actor === 'staff' ? STAFF_MOVES : INTAKE_MOVES;
    return moves.some(([from, to]) => from === prior && to === target) ? 'applied' : 'refused';
}
